import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { resolvePageContext } from '../lib/bizy-page-config';
import { getBizyPanelPreference, setBizyPanelPreference, shouldAutoOpenBizyPanel } from '../lib/panel-preferences';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { handleBizySlashKeyDown } from './use-bizy-layout';
import { useBizyPanelTracking } from './use-bizy-panel-tracking';

interface UseBizyAdminLayoutOptions {
	isMobile: boolean;
}

/**
 * Encapsulates all Bizy side-panel layout concerns for admin pages.
 *
 * Mirrors useBizyLayout but adapted for admin:
 * - No homepage auto-open (admin has no equivalent "home")
 * - No sidebar collapse (admin nav is fixed-width)
 */
export function useBizyAdminLayout({ isMobile }: UseBizyAdminLayoutOptions) {
	const isEnabled = useIsBizyEnabledForUser();
	const location = useLocation();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const isFullscreen = useBizySidePanelStore((s) => s.isFullscreen);
	const lastTrigger = useBizySidePanelStore((s) => s.lastTrigger);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const openPanel = useBizySidePanelStore((s) => s.open);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);
	const restoreOpenPanel = useCallback(() => openPanel('auto', { isHistoryDrawerOpen: false }), [openPanel]);

	// Drop to side panel on cross-layout mount when fullscreen (e.g. arriving from main app).
	// On page refresh the store starts with isFullscreen: false, so this is a no-op.
	// Uses setState directly to preserve the user's fullscreen preference in localStorage.
	useEffect(() => {
		if (useBizySidePanelStore.getState().isFullscreen) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, []);

	// Auto-open on first admin mount based on localStorage preference
	const hasAppliedPrefRef = useRef(false);
	useEffect(() => {
		if (!isEnabled || isMobile || hasAppliedPrefRef.current || useBizySidePanelStore.getState().isOpen) return;
		hasAppliedPrefRef.current = true;
		if (shouldAutoOpenBizyPanel() && getBizyPanelPreference('general') === 'open') restoreOpenPanel();
	}, [isEnabled, isMobile, restoreOpenPanel]);

	// Save preference when user manually opens or closes the panel
	const prevIsOpenRef = useRef(isPanelOpen);
	useEffect(() => {
		const wasOpen = prevIsOpenRef.current;
		prevIsOpenRef.current = isPanelOpen;
		if (wasOpen === isPanelOpen) return;
		// Persist for explicit user actions and notification-peek opens.
		// Including notification_peek means a peek-open survives a page
		// refresh (the user expects to find the panel where they left it).
		// Must stay in lockstep with use-bizy-layout.ts; AskBizyButton in
		// the admin navbar routes through useBizyAskClick →
		// openFromNotificationPeek when an unread arrives, so leaving
		// notification_peek out here would silently regress the
		// "panel survives refresh" guarantee for admin pages.
		if (lastTrigger !== 'user' && lastTrigger !== 'keyboard' && lastTrigger !== 'notification_peek') return;
		setBizyPanelPreference('general', isPanelOpen ? 'open' : 'closed');
	}, [isPanelOpen, lastTrigger]);

	// Sync page context from the current admin route
	useEffect(() => {
		if (!isEnabled || isMobile) return;
		setPageContext(resolvePageContext(location.pathname));
	}, [isEnabled, isMobile, location.pathname, setPageContext]);

	// Drop to side panel on within-layout route change when fullscreen.
	// Preserves the user's fullscreen preference so reopening restores fullscreen.
	const prevPathnameRef = useRef(location.pathname);
	useEffect(() => {
		const prev = prevPathnameRef.current;
		prevPathnameRef.current = location.pathname;
		if (prev !== location.pathname && useBizySidePanelStore.getState().isFullscreen) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, [location.pathname]);

	useBizyPanelTracking(isPanelOpen);

	// "/" toggles the panel when not typing in an input
	const handleKeyDown = useCallback((event: KeyboardEvent) => handleBizySlashKeyDown(event, () => togglePanel('keyboard')), [togglePanel]);

	useEffect(() => {
		if (!isEnabled || isMobile) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isEnabled, isMobile, handleKeyDown]);

	return { isEnabled, isPanelOpen, isFullscreen, panelWidth, isResizing, togglePanel };
}
